import { useNotify } from '@globaleris/ui.library'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumGrupoDocumento, EnumTipoDocumento } from '../../../api'
import { AtlasDocumentModel } from '../../../api/Argentia/endpoints/documents/model/atlasDocument'
import { DocumentModel } from '../../../api/Argentia/endpoints/documents/model/document'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { fileDownloadHelper } from '../../../helpers/fileDownloadHelper'
import { fileToBase64 } from '../../../helpers/fileToBase64'
import { useConfirmationDialog } from '../../../hooks'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { getActiveCompany } from '../../../redux'
import { mapDocumentsModelToUploadFileModel } from '../../documents/documents.mapper'
import {
  createDocument,
  deleteDocument,
  deleteDocumentFile,
  downloadDocument,
  getDocumentsByDocumentGroupList,
  updateDocument,
  uploadFile
} from '../../documents/documents.service'
import {
  AtlasDocumentEditPayload,
  AtlasDocumentGroupCard,
  AtlasDocumentGroupKind,
  AtlasDocumentInsertPayload
} from './components/atlasDocumentGroupCard/atlasDocumentGroupCard'

interface AtlasDocumentsPageProps {
  location?: {
    state: {
      pageProp?: string
    }
  }
}

const isAtlasUrlType = (type: EnumTipoDocumento): boolean =>
  type === EnumTipoDocumento.UrlAtlasESP || type === EnumTipoDocumento.UrlAtlasENG

const isUploadResultSuccess = (statusCode: number | string): boolean =>
  statusCode === 200 || statusCode === '200' || statusCode === 'OK'

export const AtlasDocumentsPage: React.FC<AtlasDocumentsPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'atlasDocuments'])
  const history = useHistory()
  const { info, error } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [atlasDocuments, setAtlasDocuments] = useState<AtlasDocumentModel[]>([])

  // #region Handlers Starts

  // ------------------- Header Action Buttons Handlers -----------------------------//

  const handleBackButtonClick = () => {
    history.goBack()
  }

  const handleSave = async () => {
    // Save Logic
  }

  // #endregion Handlers Ends

  // #region Functions Starts

  // sube el archivo (uploadFile v2) y devuelve el nombre final usado en storage, o undefined si falla
  // si se pasa description, el back crea el G_DOCUMENTO junto con la subida (createDocumentRecord)
  const uploadAtlasFile = async (
    file: File,
    type: EnumTipoDocumento,
    description?: string
  ): Promise<string | undefined> => {
    const fileContent = await fileToBase64(file)
    const document: DocumentModel = {
      type,
      description: file.name,
      documentDate: moment().format('YYYY-MM-DD'),
      attachment: { blobName: file.name, contenido: fileContent }
    }
    const request = mapDocumentsModelToUploadFileModel([document], type, description)
    const response = await uploadFile(request)

    const uploadedFile = response.success ? response.data?.[0] : undefined
    if (!uploadedFile || !isUploadResultSuccess(uploadedFile.statusCode)) {
      return undefined
    }

    return uploadedFile.fileName
  }

  const fetchAtlasDocuments = async () => {
    setIsFetching(true)
    await getDocumentsByDocumentGroupList([EnumGrupoDocumento.DocumentosAtlas]).then(response => {
      if (response.success && response.data !== undefined) {
        setAtlasDocuments(response.data)
      } else {
        error(t('atlasDocuments:notifications.fetchError'))
      }
    })
    setIsFetching(false)
  }

  const handleAtlasDocumentInsert = async (payload: AtlasDocumentInsertPayload) => {
    if (!payload.file) return

    // el back sube el archivo y crea el G_DOCUMENTO en la misma llamada (uploadFile con createDocumentRecord)
    const uploadedFileName = await uploadAtlasFile(payload.file, payload.type, payload.description)
    if (!uploadedFileName) {
      error(t('atlasDocuments:notifications.createError'))
      return
    }

    info(t('atlasDocuments:notifications.createSuccess'))
    fetchAtlasDocuments()
  }

  const handleAtlasUrlInsert = async (payload: AtlasDocumentInsertPayload) => {
    if (!payload.url) return
    await createDocument({
      type: payload.type,
      description: payload.description,
      documentDate: moment().format('YYYY-MM-DD'),
      companyID: getActiveCompany()?.id ?? 0,
      url: payload.url
    }).then(response => {
      if (response.success) {
        info(t('atlasDocuments:notifications.createSuccess'))
        fetchAtlasDocuments()
      } else {
        error(t('atlasDocuments:notifications.createError'))
      }
    })
  }

  const handleAtlasEdit = async (payload: AtlasDocumentEditPayload) => {
    // el archivo y el idioma/tipo son inmutables en edición (dropzone oculto), solo cambia el título
    await updateDocument({
      id: payload.id,
      type: payload.type,
      description: payload.description,
      url: payload.url
    }).then(response => {
      if (response.success) {
        info(t('atlasDocuments:notifications.updateSuccess'))
        fetchAtlasDocuments()
      } else {
        error(t('atlasDocuments:notifications.updateError'))
      }
    })
  }

  const handleAtlasDownload = async (row: AtlasDocumentModel) => {
    if (!row.id) return
    await downloadDocument(row.id).then(response => {
      if (response.success && response.data) {
        fileDownloadHelper(response.data.name, response.data)
      } else {
        error(t('atlasDocuments:notifications.downloadError'))
      }
    })
  }

  // documentos tienen archivo asociado: hay que borrar blob + fila en la misma llamada
  const handleAtlasDocumentDelete = async (row: AtlasDocumentModel) => {
    getConfirmation(ConfirmationDialogs.DELETE).then(async () => {
      await deleteDocumentFile(row.id!).then(response => {
        if (response.success) {
          info(t('atlasDocuments:notifications.deleteSuccess'))
          fetchAtlasDocuments()
        } else {
          error(t('atlasDocuments:notifications.deleteError'))
        }
      })
      closeConfirmation()
    })
  }

  // urls no tienen archivo: alcanza con borrar la fila
  const handleAtlasUrlDelete = async (row: AtlasDocumentModel) => {
    getConfirmation(ConfirmationDialogs.DELETE).then(async () => {
      await deleteDocument(row.id!).then(response => {
        if (response.success) {
          info(t('atlasDocuments:notifications.deleteSuccess'))
          fetchAtlasDocuments()
        } else {
          error(t('atlasDocuments:notifications.deleteError'))
        }
      })
      closeConfirmation()
    })
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    fetchAtlasDocuments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // #endregion Hooks Ends

  return (
    <Page layout={PageLayout.SINGLE_COLUMN} fullWidth={true}>
      <PageCenterColumn>
        <PageHeader title={t('atlasDocuments:header.title')}>
          {/*<AtlasDocumentsPageHeaderActions
            onBackButtonClick={handleBackButtonClick}
            onSaveClick={handleSave}*/}
        </PageHeader>
        <AtlasDocumentGroupCard
          kind={AtlasDocumentGroupKind.Document}
          data={atlasDocuments.filter(document => !isAtlasUrlType(document.type))}
          isLoading={isFetching}
          onRowInsert={handleAtlasDocumentInsert}
          onRowEdit={handleAtlasEdit}
          onRowDelete={handleAtlasDocumentDelete}
          onRowDownload={handleAtlasDownload}
        />
        <AtlasDocumentGroupCard
          kind={AtlasDocumentGroupKind.Url}
          data={atlasDocuments.filter(document => isAtlasUrlType(document.type))}
          isLoading={isFetching}
          onRowInsert={handleAtlasUrlInsert}
          onRowEdit={handleAtlasEdit}
          onRowDelete={handleAtlasUrlDelete}
        />
      </PageCenterColumn>
    </Page>
  )
}
